import { useEffect, useState } from 'react';

// Leaflet + OpenStreetMap tiles — a keyless map (no API key, no billing).
// Loaded from a CDN via <link>/<script> so we don't add an npm dependency
// (the private registry blocks installs — see CLAUDE.md).
const CSS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const JS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const IMG_PATH = 'https://unpkg.com/leaflet@1.9.4/dist/images/';

let promise = null;

export function loadLeaflet() {
  if (promise) return promise;
  if (window.L) return (promise = Promise.resolve(window.L));

  promise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[data-leaflet]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = CSS_URL;
      link.setAttribute('data-leaflet', '');
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = JS_URL;
    script.async = true;
    script.onload = () => {
      if (!window.L) return reject(new Error('Leaflet failed to load'));
      // Default marker icons live next to the CDN build — point at them so pins render.
      window.L.Icon.Default.imagePath = IMG_PATH;
      resolve(window.L);
    };
    script.onerror = () => reject(new Error('Leaflet failed to load'));
    document.head.appendChild(script);
  });
  return promise;
}

export function useLeaflet() {
  const [state, setState] = useState({ ready: false, error: null });
  useEffect(() => {
    let alive = true;
    loadLeaflet()
      .then(() => alive && setState({ ready: true, error: null }))
      .catch((error) => alive && setState({ ready: false, error }));
    return () => { alive = false; };
  }, []);
  return state;
}

// Keyless geocoding via OpenStreetMap's Nominatim service.
// Light, on-demand use only (usage policy: max ~1 req/sec, no bulk) — we debounce
// per card and never auto-search, so this stays well within bounds.
export function geocode(query, { signal } = {}) {
  const url =
    `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&q=${encodeURIComponent(query)}`;
  return fetch(url, { signal, headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : []))
    .then((d) => (Array.isArray(d) ? d : []));
}
